/**
 * AgreementContentStep - Generate or write agreement content
 *
 * For AI type: Shows generation UI with preview
 * For Manual type: Shows rich text editor
 * For Template type: Shows template selection
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import { FlowNavigation, StepContainer, InfoBox } from '../FlowComponents';

// Simple Rich Text Editor Component
const SimpleEditor = ({ content, onChange, placeholder, isGenerating }) => {
  const editorRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      setIsTyping(true);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      const text = editorRef.current.innerText || '';
      onChange(text);

      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 500);
    }
  }, [onChange]);

  useEffect(() => {
    if (editorRef.current && !isTyping && content) {
      if (editorRef.current.innerText !== content) {
        editorRef.current.innerText = content;
      }
    }
  }, [content, isTyping]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const applyFormat = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
  };

  return (
    <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl overflow-hidden bg-gray-50 dark:bg-[#111111]">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-white dark:bg-black border-b border-gray-200 dark:border-[#2A2A2A]">
        <button
          type="button"
          onClick={() => applyFormat('bold')}
          className="px-3 py-1.5 text-sm font-bold bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => applyFormat('italic')}
          className="px-3 py-1.5 text-sm italic bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => applyFormat('underline')}
          className="px-3 py-1.5 text-sm underline bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
        >
          U
        </button>
        <div className="w-px h-4 bg-gray-300 dark:bg-[#666666]" />
        <button
          type="button"
          onClick={() => applyFormat('insertUnorderedList')}
          className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="ml-auto text-xs text-gray-500 dark:text-[#666666]">
          {content ? content.length : 0} characters
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!isGenerating}
        onInput={handleContentChange}
        className={`p-4 bg-gray-50 dark:bg-[#111111] text-gray-900 dark:text-white focus:outline-none min-h-[300px] max-h-[50vh] overflow-y-auto ${
          isGenerating ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{ lineHeight: '1.6' }}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />

      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
          pointer-events: none;
        }
        .dark [contenteditable]:empty:before {
          color: #666666;
        }
      `}</style>
    </div>
  );
};

const AgreementContentStep = ({ data, updateData, goNext, goBack }) => {
  const { currentUser } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      const payload = {
        freelancer_name: currentUser?.displayName || 'Freelancer',
        client_name: data.clientName || '',
        scope_of_work: data.scope || '',
        price: data.price || '',
        currency: data.currency || 'USD',
        due_date: data.timeline || '',
      };

      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/generate-contract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result?.contract_text) {
        updateData({ agreementContent: result.contract_text });
        setHasGenerated(true);
      } else {
        setGenerationError('No content returned. Please try again.');
      }
    } catch (error) {
      console.error('Error generating agreement:', error);
      setGenerationError('Failed to generate agreement. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleContentChange = (content) => {
    updateData({ agreementContent: content });
  };

  const canContinue = data.agreementContent && data.agreementContent.trim().length > 50;

  // For AI mode, show generation UI first
  if (data.agreementType === 'ai' && !hasGenerated && !data.agreementContent) {
    return (
      <StepContainer>
        <div className="max-w-xl mx-auto">
          <div className="text-center space-y-6">
            {/* Summary of what will be generated */}
            <div className="bg-gray-50 dark:bg-[#0A0A0A] rounded-2xl p-6 text-left border border-gray-200 dark:border-[#1A1A1A]">
              <h3 className="text-sm font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wide mb-4">
                Agreement Summary
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-500 dark:text-[#666666] text-sm">Client:</span>
                  <span className="ml-2 text-gray-900 dark:text-white font-medium">{data.clientName}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-[#666666] text-sm">Scope:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{data.scope}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-[#666666] text-sm">Price:</span>
                  <span className="ml-2 text-gray-900 dark:text-white font-medium">
                    {data.currency} {parseFloat(data.price || 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-[#666666] text-sm">Date:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {data.timeline ? new Date(data.timeline).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {generationError && (
              <InfoBox
                title="Generation Failed"
                description={generationError}
                variant="warning"
              />
            )}

            {/* Generate button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGenerate}
              disabled={isGenerating}
              className={`w-full py-4 rounded-2xl font-medium transition-all flex items-center justify-center gap-3 ${
                isGenerating
                  ? 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 dark:text-[#666666] cursor-wait'
                  : 'bg-brand hover:bg-brand-hover text-black'
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  <span>Generating Agreement...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>Generate with AI</span>
                </>
              )}
            </motion.button>

            <p className="text-xs text-gray-500 dark:text-[#666666]">
              AI will create a professional agreement based on your details.
              You can edit the text after generation.
            </p>
          </div>

          <div className="mt-8">
            <FlowNavigation onBack={goBack} disabled={true} />
          </div>
        </div>
      </StepContainer>
    );
  }

  // Show editor (for manual mode or after generation)
  return (
    <StepContainer>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Title input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Agreement Title
          </label>
          <input
            type="text"
            placeholder="e.g., Wedding Photography Agreement"
            value={data.agreementTitle || `Agreement with ${(data.clientName || '').split(' ')[0]}`}
            onChange={(e) => updateData({ agreementTitle: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/20 focus:border-brand"
          />
        </div>

        {/* Editor */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Agreement Content
            </label>
            {data.agreementType === 'ai' && hasGenerated && (
              <button
                onClick={() => {
                  setHasGenerated(false);
                  updateData({ agreementContent: '' });
                }}
                className="text-xs text-brand hover:text-[#FFA164] transition-colors"
              >
                Regenerate
              </button>
            )}
          </div>

          <SimpleEditor
            content={data.agreementContent || ''}
            onChange={handleContentChange}
            placeholder={data.agreementType === 'manual' ? 'Write your agreement terms here...' : 'AI-generated content will appear here...'}
            isGenerating={isGenerating}
          />
        </div>

        {/* Word count and validation */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-[#666666]">
            {data.agreementContent ? data.agreementContent.split(/\s+/).filter(Boolean).length : 0} words
          </span>
          {!canContinue && data.agreementContent && (
            <span className="text-amber-500">Content must be at least 50 characters</span>
          )}
        </div>

        {/* Navigation */}
        <div className="pt-4">
          <FlowNavigation
            onBack={goBack}
            onContinue={goNext}
            disabled={!canContinue}
            continueLabel="Review Agreement"
          />
        </div>
      </div>
    </StepContainer>
  );
};

export default AgreementContentStep;
